/**
 * Geolocation Service for Cookie Consent
 * Detects user's region to apply appropriate consent defaults
 *
 * Regions:
 * - EU: GDPR - Opt-in required (all cookies OFF by default)
 * - CA: CCPA - Opt-out notice (all cookies ON, "Do Not Sell" option)
 * - OTHER: Default accept with customize option
 */

export type Region = 'EU' | 'CA' | 'OTHER';

// EU/EEA countries (GDPR)
const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA
  'IS', 'LI', 'NO',
  // UK (follows GDPR-like regulations)
  'GB'
];

// California region code
const CALIFORNIA_REGION = 'CA';
const USA_COUNTRY = 'US';

interface GeoResponse {
  country_code?: string;
  country?: string;
  region_code?: string;
  region?: string;
}

/**
 * Detect user's region using IP geolocation
 * Uses ipapi.co (free tier: 1000 requests/day)
 * Falls back to 'OTHER' if detection fails
 */
export const detectRegion = async (): Promise<Region> => {
  try {
    // Check localStorage first for cached region
    const cached = localStorage.getItem('axoix_detected_region');
    const cachedTime = localStorage.getItem('axoix_region_detected_at');

    if (cached && cachedTime) {
      // Cache valid for 24 hours
      const cacheAge = Date.now() - parseInt(cachedTime, 10);
      if (cacheAge < 24 * 60 * 60 * 1000) {
        return cached as Region;
      }
    }

    // Fetch location from ipapi.co
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      console.warn('Geolocation API returned non-OK status:', response.status);
      return 'OTHER';
    }

    const data: GeoResponse = await response.json();

    let region: Region = 'OTHER';

    // Check for EU/EEA countries
    if (data.country_code && EU_COUNTRIES.includes(data.country_code.toUpperCase())) {
      region = 'EU';
    }
    // Check for California
    else if (
      data.country_code?.toUpperCase() === USA_COUNTRY &&
      data.region_code?.toUpperCase() === CALIFORNIA_REGION
    ) {
      region = 'CA';
    }

    // Cache the result
    localStorage.setItem('axoix_detected_region', region);
    localStorage.setItem('axoix_region_detected_at', Date.now().toString());

    return region;
  } catch (error) {
    console.warn('Failed to detect region:', error);
    return 'OTHER';
  }
};

/**
 * Get default consent settings based on region
 */
export const getDefaultConsent = (region: Region) => {
  switch (region) {
    case 'EU':
      // GDPR: Everything OFF by default, user must opt-in
      return {
        essential: true,     // Always required
        analytics: false,
        marketing: false,
      };
    case 'CA':
      // CCPA: Everything ON by default, user can opt-out
      return {
        essential: true,
        analytics: true,
        marketing: true,
      };
    case 'OTHER':
    default:
      // Default: Everything ON, user can customize
      return {
        essential: true,
        analytics: true,
        marketing: true,
      };
  }
};

/**
 * Get consent banner configuration based on region
 */
export const getBannerConfig = (region: Region) => {
  switch (region) {
    case 'EU':
      return {
        title: 'Cookie Consent Required',
        message: 'We use cookies to enhance your experience. Please choose which cookies you consent to.',
        showRejectAll: true,
        defaultAction: 'customize',
        requireExplicitConsent: true,
      };
    case 'CA':
      return {
        title: 'Cookie Notice',
        message: 'We use cookies to enhance your experience. You have the right to opt-out of the sale of your personal information.',
        showRejectAll: true,
        defaultAction: 'accept',
        requireExplicitConsent: false,
        showDoNotSell: true,
      };
    case 'OTHER':
    default:
      return {
        title: 'Cookie Notice',
        message: 'We use cookies to enhance your experience. By continuing to use our site, you accept our use of cookies.',
        showRejectAll: true,
        defaultAction: 'accept',
        requireExplicitConsent: false,
      };
  }
};
